import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'

export const SW_UPDATE_EVENT = 'swUpdateAvailable'
export const SW_UPDATE_MODAL_VISIBILITY_EVENT = 'swUpdateModalVisibility'

const UPDATE_CHECK_INTERVAL = 60 * 1000

// Stages donde tiene sentido chequear nuevas versiones (builds desplegados).
const UPDATE_ENABLED_STAGES = ['prod', 'preprod', 'remotetest']

const isUpdateCheckEnabled = () =>
  process.env.NODE_ENV === 'production' &&
  UPDATE_ENABLED_STAGES.includes(process.env.REACT_APP_STAGE)

const normalizePath = url => {
  try {
    return new URL(url, window.location.origin).pathname
  } catch (e) {
    return url
  }
}

const getCurrentBuildAssets = () => {
  const scriptAssets = Array.from(document.scripts).map(script => script.src)
  const styleAssets = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map(
    link => link.href
  )

  return new Set(
    [...scriptAssets, ...styleAssets]
      .map(normalizePath)
      .filter(path => path.includes('/static/js/') || path.includes('/static/css/'))
  )
}

const getAssetManifestUrl = () => {
  const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '')
  return `${publicUrl}/asset-manifest.json?update-check=${Date.now()}`
}

const getRemoteMainAsset = async () => {
  const response = await fetch(getAssetManifestUrl(), {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache'
    }
  })

  if (!response.ok) {
    return null
  }

  const manifest = await response.json()
  const mainAsset = manifest.files && manifest.files['main.js']

  return mainAsset ? normalizePath(mainAsset) : null
}

const reloadApp = () => {
  const url = new URL(window.location.href)
  url.searchParams.set('update', Date.now())
  window.location.replace(url.toString())
}

const forceUpdateAndReload = async () => {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      await Promise.all(registrations.map(registration => registration.unregister()))
    }

    if ('caches' in window) {
      const cacheKeys = await caches.keys()
      await Promise.all(cacheKeys.map(key => caches.delete(key)))
    }
  } catch (error) {
    console.error('Error al actualizar la aplicación:', error)
  } finally {
    reloadApp()
  }
}

const UpdateRequiredModal = () => {
  const [show, setShow] = useState(false)
  const isUpdatingRef = useRef(false)

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent(SW_UPDATE_MODAL_VISIBILITY_EVENT, {
        detail: { visible: show }
      })
    )

    return () => {
      window.dispatchEvent(
        new CustomEvent(SW_UPDATE_MODAL_VISIBILITY_EVENT, {
          detail: { visible: false }
        })
      )
    }
  }, [show])

  useEffect(() => {
    const handler = () => setShow(true)
    window.addEventListener(SW_UPDATE_EVENT, handler)
    return () => window.removeEventListener(SW_UPDATE_EVENT, handler)
  }, [])

  useEffect(() => {
    if (!isUpdateCheckEnabled()) {
      return undefined
    }

    let cancelled = false
    const currentAssets = getCurrentBuildAssets()

    if (!currentAssets.size) {
      return undefined
    }

    const checkForNewBuild = () => {
      if (isUpdatingRef.current) {
        return
      }

      getRemoteMainAsset()
        .then(remoteMainAsset => {
          if (cancelled || isUpdatingRef.current || !remoteMainAsset) {
            return
          }

          if (!currentAssets.has(remoteMainAsset)) {
            setShow(true)
          }
        })
        .catch(error => {
          console.error('Error checking app version:', error)
        })
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkForNewBuild()
      }
    }

    checkForNewBuild()
    const interval = window.setInterval(checkForNewBuild, UPDATE_CHECK_INTERVAL)
    window.addEventListener('focus', checkForNewBuild)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      cancelled = true
      window.clearInterval(interval)
      window.removeEventListener('focus', checkForNewBuild)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  const handleUpdate = () => {
    if (isUpdatingRef.current) {
      return
    }

    isUpdatingRef.current = true
    setShow(false)
    forceUpdateAndReload()
  }

  if (!show) return null

  return (
    <Overlay>
      <Card>
        <Title>¡Nueva versión disponible!</Title>
        <Subtitle>La aplicación requiere actualizarse</Subtitle>
        <UpdateButton onClick={handleUpdate}>Actualizar ahora</UpdateButton>
      </Card>
    </Overlay>
  )
}

export default UpdateRequiredModal

/**
 * Styles
 */

const Overlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 99999;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
`

const Card = styled.div`
  background: ${props => props.theme.colors.brand.white};
  border-radius: 12px;
  padding: 40px 32px;
  max-width: 400px;
  width: 90%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  @media (max-width: 480px) {
    padding: 28px 20px;
    border-radius: 10px;
  }
`

const Title = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: ${props => props.theme.colors.brand.blueOmint};
  margin: 0 0 24px;

  @media (max-width: 480px) {
    font-size: 18px;
    margin: 0 0 16px;
  }
`

const Subtitle = styled.p`
  font-size: 17px;
  font-weight: 500;
  color: ${props => props.theme.colors.text.primaryText};
  margin: 0 0 32px;

  @media (max-width: 480px) {
    font-size: 15px;
    margin: 0 0 24px;
  }
`

const UpdateButton = styled.button`
  background: ${props => props.theme.colors.brand.blueOmint};
  color: ${props => props.theme.colors.brand.white};
  border: none;
  border-radius: 8px;
  padding: 14px 32px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  width: 100%;
  transition: opacity 0.2s;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    opacity: 0.88;
  }

  @media (max-width: 480px) {
    padding: 16px 24px;
    font-size: 15px;
  }
`
